import React, { FC, useState, useMemo, useEffect, useCallback } from "react";
import "./style.scss";
import CheckIcon from "../../../../../assets/icon-check.svg";
import GoBack from "../../../../../assets/AuthBack.svg";
import GoBack2 from "../../../../../assets/GoBackApp.svg";
import Search from "../../../../../assets/CountrySearch.svg";
import { useTranslation } from "react-i18next";
import { useCountries } from "hooks/useCountries";

export type PropType = {
  onClose: () => void;
  onSelect: (code: string) => void;
  specificCodes?: string[];
  returnAlpha2?: boolean;
  selectedName?: string;
  label: string;
  diffGoBackIcon?: boolean;
  usedCountries?: string[];
};

export const CountryModal: FC<PropType> = ({
  onClose,
  onSelect,
  specificCodes,
  returnAlpha2 = false,
  selectedName,
  label,
  diffGoBackIcon = false,
  usedCountries,
}) => {
  const { t } = useTranslation("app");
  const { countries, fetchFlag } = useCountries(specificCodes);
  const [selected, setSelected] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  useEffect(() => {
    if (!selected && selectedName && countries.length) {
      const found = countries.find((c) => c.name === selectedName);
      if (found) setSelected(found);
    }
  }, [countries, selectedName, selected]);

  // Debounce search term to improve performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const visibleCountries = useMemo(() => {
    const term = debouncedSearchTerm.trim().toLowerCase();
    return countries
      .filter((c) => c.name.toLowerCase().includes(term))
      .sort((a, b) => {
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();
        const aStarts = aName.startsWith(term) ? 0 : 1;
        const bStarts = bName.startsWith(term) ? 0 : 1;
        if (aStarts !== bStarts) {
          return aStarts - bStarts;
        }
        return aName.localeCompare(bName);
      });
  }, [countries, debouncedSearchTerm]);

  const handleSelect = async (country: any) => {
    try {
      const code = returnAlpha2 ? country.alpha2Code : country.alpha3Code;

      // Set selected immediately for UI feedback
      setSelected(country);

      // Fetch flag in background, don't await it
      fetchFlag(code, country.flag).catch((error) => {
        console.warn(`Failed to fetch flag for ${country.name}:`, error);
      });

      // Call onSelect immediately
      onSelect(code);

      // Close modal after a short delay to ensure selection is processed
      setTimeout(() => {
        onClose();
      }, 100);
    } catch (error) {
      console.error("Error selecting country:", error);
      // Still try to select the country even if flag fetch fails
      const code = returnAlpha2 ? country.alpha2Code : country.alpha3Code;
      setSelected(country);
      onSelect(code);
      onClose();
    }
  };

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    []
  );

  return (
    <div className="country-modal-page">
      <div className="country-modal-page-header">
        <img
          className="go-back-portfolio"
          onClick={onClose}
          src={diffGoBackIcon ? GoBack2 : GoBack}
          alt="Go Back"
        />
        <p>{label}</p>
      </div>

      <img className="country-search-icon" src={Search} alt="search" />

      <input
        type="text"
        className="country-search"
        placeholder={t("search") || "Search"}
        value={searchTerm}
        onChange={handleSearchChange}
        autoFocus
      />

      <div className="country-list">
        {visibleCountries.map((country, idx) => {
          const code = returnAlpha2 ? country.alpha2Code : country.alpha3Code;
          const isUsed = usedCountries?.includes(code);

          return (
            <div
              key={`${country.alpha3Code}-${idx}`}
              onClick={() => (isUsed ? null : handleSelect(country))}
              className="country-item"
              style={{
                opacity: isUsed ? 0.5 : 1,
              }}
            >
              <img
                className="flag"
                src={country.flag}
                alt={country.name}
                onError={(e) => {
                  // Fallback to a default flag or hide the image
                  const target = e.target as HTMLImageElement;
                  target.style.display = "none";
                  console.warn(`Failed to load flag for ${country.name}`);
                }}
              />
              <span className="country-name">{country.name}</span>
              {selected?.name === country.name && (
                <img className="check-icon" src={CheckIcon} alt="Selected" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
